import React, { useContext, useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { inboundTruckApi as api } from 'api/inboundTrucks';
import { UserContext } from 'contexts/UserContext';
import { FormComponent, Notifications } from 'shared';
import { DefaultCell } from 'shared/components/dataTable/components/tableCells';
import dayjs from 'utils/dayjsConfig';

function ScheduledTruckTableCell({ openTruckDetailsModal, time, trucks }) {
    const trucksAtTime = trucks.filter((truck) => truck.scheduledDeliveryDetails.startingHour === time);

    if (!trucksAtTime.length) {
        return null;
    }

    const getBackgroundColor = (truck) => {
        // Status-based colors
        switch (truck.status) {
            case 'PLACED':
                return '#4CAF50'; // Green
            case 'IN_TRANSIT':
                return '#FFEB3B'; // Yellow
            case 'CANCELLED':
                return '#FF9800'; // Orange
            case 'NO_SHOW':
                return '#F44336'; // Red
            case 'RESCHEDULED':
                return '#9C27B0'; // Purple
            case 'RELEASED':
            case 'RECEIVED':
            case 'RECEIVED_SYNCED':
                return '#2196F3'; // Blue
        }
    };

    return (
        <div style={{ width: '100%', padding: '5px' }}>
            {trucksAtTime.map((truck) => (
                <div
                    onClick={() => openTruckDetailsModal(truck)}
                    key={truck.id}
                    style={{
                        cursor: 'pointer',
                        width: `${100 / trucksAtTime.length}%`,
                        minWidth: '180px',
                        display: 'inline-block',
                    }}
                    className="truck-tile"
                >
                    <div
                        style={{
                            margin: '2px',
                            backgroundColor: getBackgroundColor(truck),
                            borderRadius: '4px',
                            padding: '5px',
                            overflow: 'wrap',
                        }}
                    >
                        {truck.status === 'CANCELLED' ? (
                            <div style={{ fontWeight: 'bold', textAlign: 'center' }}>CANCELLED</div>
                        ) : (
                            <div style={{ fontWeight: 'bold', textAlign: 'center' }}>{truck.deliveryType}</div>
                        )}
                        <div>
                            <span style={{ fontWeight: 'bold' }}>Door:</span>{' '}
                            {truck.dockDoorKey === 'unassigned' || !truck.dockDoorKey ? 'N/A' : truck.dockDoorKey}
                        </div>
                        <div>
                            <span style={{ fontWeight: 'bold' }}>Truck ID:</span> {truck.id}
                        </div>
                        <div>
                            <span style={{ fontWeight: 'bold' }}># POs:</span>{' '}
                            {truck.poIDs.length + truck.packagingPoIDs.length}
                        </div>
                        <div>
                            <span style={{ fontWeight: 'bold' }}># Lines:</span> {truck.numItems}
                        </div>
                        {truck.poIDs?.length > 0 && (
                            <div>
                                <span style={{ fontWeight: 'bold' }}>POs:</span> {truck.poIDs.join(', ')}
                            </div>
                        )}
                        {truck.packagingPoIDs?.length > 0 && (
                            <div>
                                <span style={{ fontWeight: 'bold' }}>Packaging POs:</span>{' '}
                                {truck.packagingPoIDs?.join(', ')}
                            </div>
                        )}
                        {truck.numPallets > 0 && (
                            <div>
                                <span style={{ fontWeight: 'bold' }}>Pallets:</span> {truck.numPallets}
                            </div>
                        )}
                        <div>
                            <span style={{ fontWeight: 'bold' }}>Carrier:</span> {truck.inboundCarrierName}
                        </div>
                        <div>
                            <span style={{ fontWeight: 'bold' }}>FBM:</span> {truck.isFBM}
                        </div>
                        {truck.oosSkus.length > 0 && (
                            <div>
                                <span style={{ fontWeight: 'bold' }}>OOS Skus</span>
                                {truck.oosSkus.map((item) => (
                                    <div key={item.sku}>
                                        Slot: {item.slot}, Sku: {item.sku} - {item.name}
                                    </div>
                                ))}
                            </div>
                        )}
                        {truck.notes && (
                            <div>
                                <span style={{ fontWeight: 'bold' }}>Notes:</span> {truck.notes}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function TruckDetailsModal({ getTrucks, onClose, open, selectedTruck, setNotification }) {
    const { user } = useContext(UserContext);
    const [editDeliveryModalOpen, setEditDeliveryModalOpen] = useState(false);
    const [flag, setFlag] = useState('');
    const [selectedRow, setSelectedRow] = useState({});
    const [truck, setTruck] = useState(selectedTruck);

    useEffect(() => {
        setTruck(selectedTruck);
    }, [selectedTruck]);

    const showTimeEditModal = (row, field) => {
        setSelectedRow(row);
        setFlag(field);
        setEditDeliveryModalOpen(true);
    };

    const setEditedTruck = (row) => {
        row.scheduledDelivery = row.scheduledDeliveryTime
            ? dayjs(row.scheduledDeliveryTime.date).format('MM/DD/YYYY hh:mm a')
            : null;
        row.checkIn = row.checkInTime ? dayjs(row.checkInTime.date).format('MM/DD/YYYY hh:mm a') : null;
        row.checkOut = row.checkOutTime ? dayjs(row.checkOutTime.date).format('MM/DD/YYYY hh:mm a') : null;
        row.dockIn = row.dockInTime ? dayjs(row.dockInTime.date).format('MM/DD/YYYY hh:mm a') : null;
        row.unloadStart = row.unloadStartTime ? dayjs(row.unloadStartTime.date).format('MM/DD/YYYY hh:mm a') : null;
        row.unloadEnd = row.unloadEndTime ? dayjs(row.unloadEndTime.date).format('MM/DD/YYYY hh:mm a') : null;

        row.scheduledDeliveryDetails = {
            date: row.scheduledDeliveryTime ? dayjs(row.scheduledDeliveryTime.date).format('MM/DD/YYYY') : null,
            startingHour: row.scheduledDeliveryTime
                ? dayjs(row.scheduledDeliveryTime.date).startOf('hour').format('hh:mm a')
                : null,
            time: row.scheduledDeliveryTime ? dayjs(row.scheduledDeliveryTime.date).format('hh:mm a') : null,
        };

        row.multiStopTruck = row.multiStopTruck ? 'yes' : 'no';
        row.scheduled = row.unscheduled ? 'no' : 'yes';

        const twoHoursInMs = 2 * (60 * 60 * 1000);
        const scheduledDeliveryTimeAsDate =
            !!row.scheduledDeliveryTime && row.scheduledDeliveryTime.date
                ? new Date(row.scheduledDeliveryTime.date)
                : new Date();
        const timeDifference = new Date() - scheduledDeliveryTimeAsDate;
        row.isTruckOnTime = !!row.checkInTime || timeDifference < twoHoursInMs || timeDifference < 0 ? 'Yes' : 'No';

        row.isFBM = row.fbmPartner != null && row.fbmPartner.trim() != '' ? 'Yes' : 'No';
        setTruck(row);
    };

    function EditDateModal({ closeModal, flag, rowData }) {
        const [error, setError] = useState(null);
        const [date, setDate] = useState(null);
        const [time, setTime] = useState(null);
        const [loading, setLoading] = useState(false);

        const formFields = [
            {
                name: 'truckID',
                initialValue: rowData.id,
                inputElement: 'textField',
                label: 'Truck ID',
                gridValue: 4,
                inputProps: { readOnly: true },
            },
            {
                name: 'date',
                initialValue: date,
                inputElement: 'date',
                label: 'Date',
                gridValue: 4,
                inputProps: { required: true },
            },
            {
                name: 'time',
                initialValue: time,
                inputElement: 'time',
                label: 'Time',
                gridValue: 4,
                inputProps: { required: true },
            },
            {
                name: 'bolCompliant',
                inputElement: 'checkbox',
                label: 'BOL Compliant',
                gridValue: 4,
                display: flag === 'checkIn',
            },
        ];

        useEffect(() => {
            setDate(dayjs(rowData[flag]).format('YYYY-MM-DD'));
            setTime(dayjs(rowData[flag]).format('HH:mm'));
        }, []);

        return (
            <Modal open onClose={async () => await closeModal()}>
                <div
                    style={{
                        outline: 0,
                        backgroundColor: 'white',
                        width: '700px',
                        margin: '10% auto',
                        padding: '10px',
                    }}
                >
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <FormComponent
                            formFields={formFields}
                            onSubmit={async (formData) => {
                                setLoading(true);
                                const item = {};

                                item[`${flag}Date`] = formData.date;
                                item[`${flag}Time`] = formData.time;

                                if (formData.bolCompliant) {
                                    item.bolCompliant = formData.bolCompliant;
                                }

                                const response = await api.editTruckFacilityStopDetails(rowData.facilityStopID, item);
                                if (response.status === false) {
                                    let message = 'Error modifying truck';
                                    if (response) {
                                        message += `: ${response.msg}`;
                                    }
                                    setError(`Error updating ${message}`);
                                } else {
                                    setEditedTruck(response.data.editedTruck);
                                    closeModal();
                                }
                                setLoading(false);
                            }}
                        />
                    )}
                    {error && <div style={{ color: 'red' }}>{error}</div>}
                </div>
            </Modal>
        );
    }

    const markTruckTimeStamp = async (truck, field) => {
        const now = new Date();

        const currentDate = now.toISOString().split('T')[0];
        const timeString = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });

        const [time, modifier] = timeString.split(' ');
        let [hours, minutes] = time.split(':').map(Number);

        if (modifier === 'PM' && hours !== 12) {
            hours += 12;
        } else if (modifier === 'AM' && hours === 12) {
            hours = 0;
        }

        const time24hr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

        const item = {
            [`${field}Date`]: currentDate,
            [`${field}Time`]: time24hr,
        };

        const response = await api.editTruckFacilityStopDetails(truck.facilityStopID, item);
        if (response.status === false) {
            setNotification({ text: 'Error marking truck time stamp', level: 'error' });
        } else {
            setNotification({ text: 'Truck time stamp marked', level: 'success' });
            onClose();
            getTrucks();
        }
    };

    const columns = [
        {
            accessor: 'dockDoor',
            Header: 'Dock Door',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        { accessor: 'multiStopTruck', Header: 'Multi Stop Truck', editable: false },
        { accessor: 'scheduled', Header: 'Scheduled?', editable: false },
        { accessor: 'deliveryType', Header: 'DeliveryType' },
        { accessor: 'referenceNumber', Header: 'Ship Reference #' },
        { accessor: 'vendorNames', Header: 'Vendor Name' },
        { accessor: 'scheduledDelivery', Header: 'Scheduled Delivery', editable: false },
        { accessor: 'inboundCarrierName', Header: 'Carrier', editable: false },
        { accessor: 'type', Header: 'Type', editable: false },
        { accessor: 'minTemperature', Header: 'Min Temperature', editable: false },
        { accessor: 'maxTemperature', Header: 'Max Temperature', editable: false },
        {
            accessor: 'actualTemperature',
            Header: 'Actual Temperature',
            editable: true,
            cellStyle: (row) => (row.isTemperatureOutOfRange ? { color: 'red' } : {}),
        },
        { accessor: 'numPallets', Header: 'Num Pallets' },
        {
            accessor: 'checkIn',
            Header: 'Check In Time',
            editable: true,
            ...(user.hasTimestampTrucksRole
                ? {
                      customEdit: (row) => showTimeEditModal(row, 'checkIn'),
                  }
                : {}),
        },
        {
            accessor: 'dockIn',
            Header: 'Dock In Time',
            editable: true,
            ...(user.hasTimestampTrucksRole
                ? {
                      customEdit: (row) => showTimeEditModal(row, 'dockIn'),
                  }
                : {}),
        },
        {
            accessor: 'unloadStart',
            Header: 'Unload Start Time',
            editable: true,
            ...(user.hasTimestampTrucksRole
                ? {
                      customEdit: (row) => showTimeEditModal(row, 'unloadStart'),
                  }
                : {}),
        },
        {
            accessor: 'unloadEnd',
            Header: 'Unload End Time',
            editable: true,
            ...(user.hasTimestampTrucksRole
                ? {
                      customEdit: (row) => showTimeEditModal(row, 'unloadEnd'),
                  }
                : {}),
        },
        {
            accessor: 'checkOut',
            Header: 'Check Out Time',
            editable: true,
            ...(user.hasTimestampTrucksRole
                ? {
                      customEdit: (row) => showTimeEditModal(row, 'checkOut'),
                  }
                : {}),
        },
        {
            accessor: 'bolCompliant',
            Header: 'BOL Compliant',
            type: 'checkbox',
            editable: true,
        },
        { accessor: 'notes', Header: 'Notes', editable: true },
        { accessor: 'equipmentType', Header: 'EquipmentType' },
        { accessor: 'status', Header: 'Truck Status' },
        { accessor: 'fbmPartner', Header: 'FBM' },
    ];
    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={() => {
                onClose();
            }}
        >
            {editDeliveryModalOpen && (
                <EditDateModal
                    rowData={selectedRow}
                    flag={flag}
                    closeModal={async () => {
                        setEditDeliveryModalOpen(false);
                    }}
                />
            )}
            <DialogTitle>
                <Grid container>
                    <Grid item xs={10} style={{ textAlign: 'center' }}>
                        Inbound Truck {truck.id} {truck.priorityPoIDs?.length > 0 && ` - PRIORITY`}
                    </Grid>
                    <Grid item xs={2}>
                        <Button
                            variant="contained"
                            onClick={async () => {
                                try {
                                    await api.downloadTruckShipList(truck.id, user.facilityID, user.getFacilityName());
                                } catch (e) {
                                    setNotification({ text: e.toString(), level: 'error' });
                                }
                            }}
                        >
                            Ship List
                        </Button>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent>
                <TableContainer>
                    <Table>
                        <TableBody>
                            {columns.map((col) => {
                                if (truck.deliveryType !== 'DELIVERED' && col.accessor === 'vendorName') {
                                    return null;
                                }

                                return (
                                    <TableRow key={col.accessor}>
                                        <TableCell variant="head">{col.Header}</TableCell>
                                        {['checkIn', 'checkOut', 'dockIn', 'unloadStart', 'unloadEnd'].includes(
                                            col.accessor,
                                        ) && !user.hasTimestampTrucksRole ? (
                                            <TableCell>
                                                {truck[col.accessor] ? (
                                                    truck[col.accessor]
                                                ) : (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => markTruckTimeStamp(truck, col.accessor)}
                                                    >
                                                        {col.Header.split(' ').slice(0, -1).join(' ')}
                                                    </Button>
                                                )}
                                            </TableCell>
                                        ) : (
                                            <TableCell>
                                                <DefaultCell
                                                    cell={{ value: truck[col.accessor] }}
                                                    row={{ original: truck }}
                                                    column={{ ...col, id: col.accessor }}
                                                    saveEdit={async ({ field, id, value }) => {
                                                        const item = { [field]: value };
                                                        const facilityStopFields = [
                                                            'dockDoor',
                                                            'actualTemperature',
                                                            'notes',
                                                            'bolCompliant',
                                                        ];
                                                        let response;
                                                        if (facilityStopFields.includes(field)) {
                                                            response = await api.editTruckFacilityStopDetails(
                                                                truck.facilityStopID,
                                                                item,
                                                            );
                                                        } else {
                                                            response = await api.editTruckDetails(id, item);
                                                        }
                                                        if (response.status === false) {
                                                            let message = 'Error modifying truck';
                                                            if (response.msg) {
                                                                message += `: ${response.msg}`;
                                                            }
                                                            setNotification({ text: message, level: 'error' });
                                                        } else {
                                                            setNotification({
                                                                text: 'Truck modified!',
                                                                level: 'success',
                                                            });
                                                            await getTrucks();
                                                            await onClose();
                                                        }
                                                    }}
                                                    editable={col.editable === true}
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
}

function DailyTruckSchedule({ date, getTrucks, loaded, setNotification, trucks }) {
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedTruck, setSelectedTruck] = useState({});

    const timeslots = [];
    new Array(48).fill().forEach((acc, index) => {
        timeslots.push(
            dayjs()
                .startOf('day')
                .set({ hour: Math.floor(index / 2), minute: (index % 2) * 30 })
                .format('hh:mm a'),
        );
    });

    if (!loaded) {
        return null;
    }

    if (!trucks.length) {
        return <div>No trucks found for {date}</div>;
    }

    const openTruckDetailsModal = (truck) => {
        setSelectedTruck(truck);
        setDetailModalOpen(true);
    };

    return (
        <>
            <TruckDetailsModal
                setNotification={setNotification}
                getTrucks={getTrucks}
                selectedTruck={selectedTruck}
                open={detailModalOpen && selectedTruck && selectedTruck.hasOwnProperty('id')}
                onClose={async () => {
                    setSelectedTruck({});
                    await getTrucks();
                    setDetailModalOpen(false);
                }}
            />
            <TableContainer className="tscroll" component={Paper}>
                <Table
                    style={{
                        tableLayout: 'fixed',
                        width: '100%',
                        border: '1px solid black',
                        borderCollapse: 'collapse',
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell
                                className="time-col"
                                style={{
                                    width: '170px',
                                    textAlign: 'center',
                                    position: 'sticky',
                                    left: 0,
                                    backgroundColor: '#ddd',
                                    border: '1px solid black',
                                    borderCollapse: 'collapse',
                                }}
                            >
                                <h4>Appointment Time</h4>
                            </TableCell>
                            <TableCell
                                style={{
                                    textAlign: 'center',
                                    border: '1px solid black',
                                    borderCollapse: 'collapse',
                                }}
                            >
                                <h3>Appointments</h3>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {timeslots.map((time, index) => (
                            <TableRow key={index}>
                                <TableCell
                                    className="time-col"
                                    style={{
                                        position: 'sticky',
                                        left: 0,
                                        backgroundColor: '#ddd',
                                        border: '1px solid black',
                                        borderCollapse: 'collapse',
                                    }}
                                >
                                    <Grid container>
                                        <Grid item sm={6}>
                                            {time}
                                        </Grid>
                                        <Grid item sm={6}>
                                            {timeslots[index + 1] ? timeslots[index + 1] : timeslots[0]}
                                        </Grid>
                                    </Grid>
                                </TableCell>
                                <TableCell
                                    style={{
                                        border: '1px solid black',
                                        borderCollapse: 'collapse',
                                    }}
                                >
                                    <ScheduledTruckTableCell
                                        time={time}
                                        trucks={trucks}
                                        openTruckDetailsModal={openTruckDetailsModal}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <h3>Color Key</h3>
            <>
                <div style={{ width: '15%', display: 'inline-block' }}>
                    <div
                        style={{
                            height: '25px',
                            width: '25px',
                            border: '1px solid black',
                            backgroundColor: '#4CAF50',
                            display: 'inline-block',
                        }}
                    />
                    <span style={{ fontSize: '13px' }}> Placed</span>
                </div>
                <div style={{ width: '15%', display: 'inline-block' }}>
                    <div
                        style={{
                            height: '25px',
                            width: '25px',
                            border: '1px solid black',
                            backgroundColor: '#FFEB3B',
                            display: 'inline-block',
                        }}
                    />
                    <span style={{ fontSize: '13px' }}> In Transit</span>
                </div>
                <div style={{ width: '15%', display: 'inline-block' }}>
                    <div
                        style={{
                            height: '25px',
                            width: '25px',
                            border: '1px solid black',
                            backgroundColor: '#FF9800',
                            display: 'inline-block',
                        }}
                    />
                    <span style={{ fontSize: '13px' }}> Cancelled</span>
                </div>
                <div style={{ width: '15%', display: 'inline-block' }}>
                    <div
                        style={{
                            height: '25px',
                            width: '25px',
                            border: '1px solid black',
                            backgroundColor: '#F44336',
                            display: 'inline-block',
                        }}
                    />
                    <span style={{ fontSize: '13px' }}> No Show</span>
                </div>
                <div style={{ width: '15%', display: 'inline-block' }}>
                    <div
                        style={{
                            height: '25px',
                            width: '25px',
                            border: '1px solid black',
                            backgroundColor: '#9C27B0',
                            display: 'inline-block',
                        }}
                    />
                    <span style={{ fontSize: '13px' }}> Rescheduled</span>
                </div>
                <div style={{ width: '15%', display: 'inline-block' }}>
                    <div
                        style={{
                            height: '25px',
                            width: '25px',
                            border: '1px solid black',
                            backgroundColor: '#2196F3',
                            display: 'inline-block',
                        }}
                    />
                    <span style={{ fontSize: '13px' }}> Released/Received</span>
                </div>
            </>
        </>
    );
}

function InboundTrucksSchedule() {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [date, setDate] = useState(dayjs().format('MM/DD/YYYY'));
    const [trucks, setTrucks] = useState([]);
    const [scheduledTrucks, setScheduledTrucks] = useState({ dockDoorKeys: [], trucksOnDate: [] });
    const [selectDateModalOpen, setSelectDateModalOpen] = useState(false);
    const [trucksLoaded, setTrucksLoaded] = useState(false);
    const [scheduledTrucksOnDate, setScheduledTrucksOnDate] = useState([]);
    const [carriers, setCarriers] = useState([]);
    const user = useContext(UserContext);

    const getOptionsForInboundTrucks = async () => {
        const res = await api.getInboundOptions();
        if (!res.status) {
            return;
        }
        setCarriers(res.data.carriers.map((item) => ({ text: item.name, value: item.id })));
    };

    const decrementDate = () => {
        setDate(dayjs(date).subtract(1, 'days').format('MM/DD/YYYY'));
    };

    const incrementDate = () => {
        setDate(dayjs(date).add(1, 'days').format('MM/DD/YYYY'));
    };

    // Load
    const searchInboundTrucks = async ({ searchBy, searchTerm }) => {
        const response = await api.getTrucks(user.getFacilityID(), {});

        if (response.status === false) {
            setNotification({ text: `No trucks found ${response.msg}`, level: 'error' });
            return;
        }

        if (response.data.rows) {
            response.data.rows.map((row) => {
                row.scheduledDelivery = row.scheduledDeliveryTime
                    ? dayjs(row.scheduledDeliveryTime.date).format('MM/DD/YYYY hh:mm a')
                    : null;
                row.checkIn = row.checkInTime ? dayjs(row.checkInTime.date).format('MM/DD/YYYY hh:mm a') : null;
                row.checkOut = row.checkOutTime ? dayjs(row.checkOutTime.date).format('MM/DD/YYYY hh:mm a') : null;
                row.dockIn = row.dockInTime ? dayjs(row.dockInTime.date).format('MM/DD/YYYY hh:mm a') : null;
                row.unloadStart = row.unloadStartTime
                    ? dayjs(row.unloadStartTime.date).format('MM/DD/YYYY hh:mm a')
                    : null;
                row.unloadEnd = row.unloadEndTime ? dayjs(row.unloadEndTime.date).format('MM/DD/YYYY hh:mm a') : null;

                row.scheduledDeliveryDetails = {
                    date: row.scheduledDeliveryTime ? dayjs(row.scheduledDeliveryTime.date).format('MM/DD/YYYY') : null,
                    startingHour: row.scheduledDeliveryTime
                        ? dayjs(row.scheduledDeliveryTime.date).startOf('hour').format('hh:mm a')
                        : null,
                    time: row.scheduledDeliveryTime ? dayjs(row.scheduledDeliveryTime.date).format('hh:mm a') : null,
                };

                row.multiStopTruck = row.multiStopTruck ? 'yes' : 'no';
                row.scheduled = row.unscheduled ? 'no' : 'yes';

                const twoHoursInMs = 2 * (60 * 60 * 1000);
                const scheduledDeliveryTimeAsDate =
                    !!row.scheduledDeliveryTime && row.scheduledDeliveryTime.date
                        ? new Date(row.scheduledDeliveryTime.date)
                        : new Date();
                const timeDifference = new Date() - scheduledDeliveryTimeAsDate;
                row.isTruckOnTime =
                    !!row.checkInTime || timeDifference < twoHoursInMs || timeDifference < 0 ? 'Yes' : 'No';

                row.isFBM = row.fbmPartner != null && row.fbmPartner.trim() != '' ? 'Yes' : 'No';

                return row;
            });
        }

        setTrucks(response.data.rows);
        setTrucksLoaded(true);
    };

    useEffect(() => {
        searchInboundTrucks({});
        getOptionsForInboundTrucks();
    }, []);

    useEffect(() => {
        const trucksOnDate = trucks.filter((truck) => truck.scheduledDeliveryDetails.date === date);

        const dockDoorMap = {};
        trucksOnDate.forEach((truck) => {
            if (!truck.dockDoor) {
                truck.dockDoorKey = 'unassigned';
            } else {
                truck.dockDoorKey = truck.dockDoor;
            }
            if (!dockDoorMap.hasOwnProperty(truck.dockDoorKey)) {
                dockDoorMap[truck.dockDoorKey] = [];
            }
            dockDoorMap[truck.dockDoorKey].push(truck);
        });

        const dockDoorKeys = Object.keys(dockDoorMap).sort((a, b) => {
            if (a === 'unassigned') {
                return -1;
            }
            return a - b;
        });

        setScheduledTrucks({ dockDoorKeys, trucksOnDate });
        setScheduledTrucksOnDate(trucksOnDate);
    }, [trucks, date]);

    return (
        <div>
            <style>
                {`
                @media print {
                    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .tscroll { overflow: visible !important; height: auto !important; }
                    .no-print { display: none !important; }
                    .print-only { display: block !important; }
                    table, th, td { border: 1px solid #000 !important; border-collapse: collapse !important; }
                    thead th { background: #ddd !important; }
                    .time-col { position: static !important; background: #ddd !important; }
                    .truck-tile { break-inside: avoid; page-break-inside: avoid; }
                }
                @media screen {
                    .print-only { display: none; }
                }
                `}
            </style>
            <Notifications options={notification} />
            <Dialog
                open={selectDateModalOpen}
                maxWidth="md"
                onClose={() => {
                    setSelectDateModalOpen(false);
                }}
            >
                <DialogContent>
                    <FormComponent
                        formFields={[
                            {
                                name: 'date',
                                label: 'Date',
                                inputElement: 'date',
                                gridValue: 10,
                                initialValue: dayjs(date).format('YYYY-MM-DD'),
                            },
                        ]}
                        onSubmit={async (formData) => {
                            setDate(dayjs(formData.date).format('MM/DD/YYYY'));
                            setSelectDateModalOpen(false);
                        }}
                    />
                </DialogContent>
            </Dialog>
            <h2>
                <Grid container>
                    <Grid item xs={12} md={3}>
                        Inbound Trucks Schedule
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <IconButton className="no-print" onClick={decrementDate}>
                            <ChevronLeftIcon />
                        </IconButton>
                        {dayjs(date).format('dddd MM/DD/YYYY')}
                        <IconButton className="no-print" onClick={incrementDate}>
                            <ChevronRightIcon />
                        </IconButton>

                        <div className="no-print" style={{ display: 'inline-block', paddingLeft: '100px' }}>
                            <Button
                                onClick={() => {
                                    setSelectDateModalOpen(true);
                                }}
                            >
                                Select Date
                            </Button>
                        </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            className="no-print"
                            onClick={() => {
                                searchInboundTrucks({});
                            }}
                        >
                            Refresh Data
                        </Button>
                    </Grid>
                </Grid>
            </h2>
            <DailyTruckSchedule
                setNotification={setNotification}
                getTrucks={async () => {
                    await searchInboundTrucks({});
                }}
                trucks={scheduledTrucksOnDate}
                date={date}
                loaded={trucksLoaded}
            />
        </div>
    );
}

export { InboundTrucksSchedule };
